'use client'
import React, { useState, useRef, useEffect } from 'react'
import { Dialog, DialogContent } from '@dc-shared-web/components/shadcn/dialog'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { cn } from '@dc-shared-web/lib'
import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import {
  faArrowUpRightAndArrowDownLeftFromCenter,
  faArrowDownLeftAndArrowUpRightToCenter,
} from '@document-crunch/pro-regular-svg-icons'

interface TableArtifactProps {
  children: React.ReactNode
  className?: string
}

const TableArtifact: React.FC<TableArtifactProps> = ({
  children,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const divRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const elementRef = tableRef.current || divRef.current
        if (elementRef) {
          const elementWidth = elementRef.scrollWidth
          setIsOverflowing(elementWidth > containerWidth)
        }
      }
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [children])

  const TableContent = () => {
    // Check if children contains table parts that need to be wrapped
    const hasTableParts = React.Children.toArray(children).some((child) => {
      if (React.isValidElement(child) && typeof child.type === 'string') {
        const tagName = child.type.toLowerCase()
        return ['thead', 'tbody', 'tfoot', 'tr'].includes(tagName)
      }
      return false
    })

    const tableClasses = cn(
      isExpanded ? 'tw-w-auto' : 'tw-w-full',
      'tw-border-collapse',
      '[&_th]:tw-border [&_th]:tw-border-gray-300 [&_th]:tw-bg-gray-100 [&_th]:tw-px-4 [&_th]:tw-py-2 [&_th]:tw-text-left [&_th]:tw-font-medium',
      '[&_td]:tw-border [&_td]:tw-border-gray-300 [&_td]:tw-px-4 [&_td]:tw-py-2',
      '[&_tr:nth-child(even)]:tw-bg-gray-50'
    )

    // If we have table parts, wrap them in a table
    if (hasTableParts) {
      return (
        <table ref={tableRef} className={tableClasses}>
          {children}
        </table>
      )
    }

    // Check if the child is already a complete table
    const firstChild = React.Children.toArray(children)[0]
    if (
      React.isValidElement(firstChild) &&
      typeof firstChild.type === 'string' &&
      firstChild.type.toLowerCase() === 'table'
    ) {
      return React.cloneElement(firstChild, {
        ...firstChild.props,
        ref: tableRef,
        className: cn(firstChild.props.className, tableClasses),
      })
    }

    // Default: wrap in a div with table styles applied to nested tables
    return (
      <div
        ref={divRef}
        className={cn(
          isExpanded
            ? 'tw-w-fit [&_table]:tw-w-auto'
            : 'tw-w-full [&_table]:tw-w-full',
          '[&_table]:tw-border-collapse',
          '[&_th]:tw-border [&_th]:tw-border-gray-300 [&_th]:tw-bg-gray-100 [&_th]:tw-px-4 [&_th]:tw-py-2 [&_th]:tw-text-left [&_th]:tw-font-medium',
          '[&_td]:tw-border [&_td]:tw-border-gray-300 [&_td]:tw-px-4 [&_td]:tw-py-2',
          '[&_tr:nth-child(even)]:tw-bg-gray-50'
        )}
      >
        {children}
      </div>
    )
  }

  return (
    <>
      <div
        ref={containerRef}
        className={cn(
          'tw-relative tw-my-4 tw-rounded-lg tw-border tw-border-gray-200',
          'tw-bg-gray-50 tw-p-2',
          className
        )}
      >
        {(isOverflowing || isExpanded) && (
          <div className='tw-absolute tw-right-2 tw-top-2 tw-z-10'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setIsExpanded(true)}
              className='tw-h-8 tw-w-8 tw-p-0'
              title='Expand table'
            >
              <Icon
                icon={faArrowUpRightAndArrowDownLeftFromCenter}
                color={IconColor.darkPrimary}
                className='tw-h-4 tw-w-4'
              />
            </Button>
          </div>
        )}

        <div className='tw-overflow-x-auto tw-overflow-y-hidden'>
          <TableContent />
        </div>
      </div>

      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent className='!tw-max-w-none !tw-w-auto'>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setIsExpanded(false)}
            className='tw-absolute tw-right-2 tw-top-2 tw-h-8 tw-w-8 tw-p-0 tw-z-10'
            title='Close'
          >
            <Icon
              icon={faArrowDownLeftAndArrowUpRightToCenter}
              color={IconColor.darkPrimary}
              className='tw-h-4 tw-w-4'
            />
          </Button>
          <div className='tw-p-5'>
            <div className='tw-overflow-auto tw-max-h-[80vh] tw-p-1'>
              <TableContent />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export const TableWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <TableArtifact>{children}</TableArtifact>
}
